import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatApiError } from '@/lib/api';
import { AUTH } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const BG_URL =
    'https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHRlY2huaWNhbCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NDcwNDUxOXww&ixlib=rb-4.1.0&q=85';

export default function LoginPage() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (user && user !== null && user !== false) {
        return <Navigate to={loc.state?.from || '/'} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(email, password, remember);
            toast.success('Welcome back');
            nav(loc.state?.from || '/', { replace: true });
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2 bg-background">
            {/* Visual side */}
            <div
                className="hidden md:flex relative border-r border-border overflow-hidden"
                style={{
                    backgroundImage: `url(${BG_URL})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="relative z-10 p-12 flex flex-col justify-between text-white">
                    <div className="flex items-center gap-2">
                        <div className="h-10 w-10 grid place-items-center rounded-sm bg-primary text-primary-foreground font-display font-black text-lg">
                            ⌘
                        </div>
                        <div>
                            <div className="font-display text-lg font-black tracking-tight">
                                SPACES PANEL
                            </div>
                        </div>
                    </div>
                    <div className="max-w-lg">
                        <h1 className="font-display text-4xl lg:text-5xl font-black leading-[1.02] tracking-tight">
                            Browse every Space.
                            <br />
                            <span className="text-primary">Never touch a byte.</span>
                        </h1>
                        <p className="mt-6 text-white/70 text-sm leading-relaxed max-w-sm">
                            A hardened, read-only management panel for teams that need visibility
                            into DigitalOcean Spaces without exposing write access to end users.
                        </p>
                        <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
                            <ShieldCheck size={14} />
                            zero-write · role-based access · audit trail
                        </div>
                    </div>
                    <div className="grid-bg h-24 rounded-sm border border-white/10" />
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">
                            Access the Panel
                        </h2>
                        <p className="text-sm text-muted-foreground mt-2">
                            Authenticate with your assigned credentials.
                        </p>
                    </div>

                    <form onSubmit={submit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm">
                                Email
                            </Label>
                            <Input
                                id="email"
                                data-testid={AUTH.login.emailInput}
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@company.com"
                                required
                                className="rounded-sm border-border h-11"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-sm">
                                Password
                            </Label>
                            <Input
                                id="password"
                                data-testid={AUTH.login.passwordInput}
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="rounded-sm border-border h-11"
                            />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <label className="flex items-center gap-2 cursor-pointer select-none text-muted-foreground">
                                <Checkbox
                                    checked={remember}
                                    onCheckedChange={(v) => setRemember(!!v)}
                                    data-testid={AUTH.login.rememberInput}
                                    className="rounded-sm"
                                />
                                Remember me
                            </label>
                            <Link
                                to="/forgot-password"
                                data-testid={AUTH.login.forgotLink}
                                className="text-primary hover:underline"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        {error && (
                            <div
                                data-testid={AUTH.login.errorText}
                                className="text-sm text-destructive font-mono border border-destructive/40 bg-destructive/10 rounded-sm px-3 py-2"
                            >
                                {error}
                            </div>
                        )}
                        <Button
                            type="submit"
                            data-testid={AUTH.login.submitButton}
                            disabled={loading}
                            className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={16} /> Signing in…
                                </>
                            ) : (
                                'Sign in →'
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
